import { PullService } from './servicePull'
import type { Identifiable } from '~/models/identifiable'
import { getDocs, query, where, getDoc } from 'firebase/firestore'
import type { AproData } from '~/models'

export interface AppUserIdsByAproId extends Identifiable {
  appUserId: string | undefined
}

export class ExternalUserDataPullService extends PullService<
  AppUserIdsByAproId,
  AppUserIdsByAproId[]
> {
  private collectionGroup
  private doc
  private firestoreGenericConverter

  constructor() {
    super()
    const { collectionGroup, doc, firestoreGenericConverter } = useFirebase()
    this.collectionGroup = collectionGroup
    this.doc = doc
    this.firestoreGenericConverter = firestoreGenericConverter
  }

  async getAproIdByUserId(userId: string): Promise<string> {
    const doc = this.doc(
      'AppUsers',
      userId,
      'ExternalUserData',
      'AproData',
    ).withConverter(this.firestoreGenericConverter<AproData>())

    const aproData = (await getDoc(doc)).data()

    return aproData?.PatientId ?? ''
  }

  public async getAproIdsByUserIds(
    userIds: string[],
  ): Promise<Record<string, string>> {
    const aproIdPromises = userIds.map(async userId => ({
      userId,
      aproId: await this.getAproIdByUserId(userId),
    }))

    const results = await Promise.all(aproIdPromises)

    const acc: Record<string, string> = {}
    for (const { userId, aproId } of results) {
      acc[userId] = aproId
    }
    return acc
  }

  async getAppUserIdsByAproId(aproId: string): Promise<AppUserIdsByAproId> {
    const externalUserDataQuery = query(
      this.collectionGroup('ExternalUserData'),
      where('PatientId', '==', aproId),
    )

    const { docs } = await getDocs(externalUserDataQuery)

    if (docs.length === 0) {
      return {
        appUserId: undefined,
        id: '0',
      }
    }

    const appUserId = docs[0].ref.parent.parent?.id

    return {
      appUserId,
      id: docs[0].id,
    }
  }

  async isPatientLinkActive(userId: string): Promise<boolean> {
    const doc = this.doc(
      'AppUsers',
      userId,
      'ExternalUserData',
      'AproData',
    ).withConverter(this.firestoreGenericConverter<AproData>())

    const aproData = (await getDoc(doc)).data()
    const patientLinkStatus = aproData?.PatientLink?.Status

    return patientLinkStatus === 'Accepted'
  }
}
